import QRCodeStyling, { type Options } from "qr-code-styling";
import { jsPDF } from "jspdf";
import "svg2pdf.js";
import type { QRCodeRecord } from "../../types/marketing";

export function createQR(record: QRCodeRecord, size = 280) {
  if (!record.qr_value)
    throw new Error("Save a valid destination before rendering this code.");
  const frame = record.frame_style !== "none";
  const options: Options = {
    type: "svg",
    width: size,
    height: size,
    data: record.qr_value,
    margin: Math.ceil(size * (frame ? 0.16 : 0.14)),
    qrOptions: { errorCorrectionLevel: "H" },
    shape: record.qr_shape === "circle" ? "circle" : "square",
    image: record.logo_url || undefined,
    imageOptions: {
      crossOrigin: "anonymous",
      imageSize: 0.25,
      margin: 5,
      saveAsBlob: true,
    },
    dotsOptions: {
      color: record.dots_color,
      type: (record.dots_style === "squares"
        ? "square"
        : record.dots_style) as NonNullable<Options["dotsOptions"]>["type"],
    },
    cornersSquareOptions: {
      color: record.marker_border_color,
      type:
        record.marker_border_style === "circle"
          ? "dot"
          : record.marker_border_style === "rounded"
            ? "extra-rounded"
            : "square",
    },
    cornersDotOptions: {
      color: record.marker_center_color,
      type: record.marker_center_style,
    },
    backgroundOptions: {
      color: record.background_color,
      round: record.qr_shape === "rounded" ? 0.12 : 0,
    },
  };
  const qr = new QRCodeStyling(options);
  qr.applyExtension((svg) => {
    const ns = "http://www.w3.org/2000/svg";
    if (frame) {
      const border = document.createElementNS(ns, "rect");
      const inset = size * 0.015;
      for (const [key, value] of Object.entries({
        x: inset,
        y: inset,
        width: size - inset * 2,
        height: size - inset * 2,
        rx: record.qr_shape === "rounded" ? size * 0.06 : 0,
        fill: "none",
        stroke: record.dots_color,
        "stroke-width": size * (record.frame_style === "frame" ? 0.025 : 0.008),
      }))
        border.setAttribute(key, String(value));
      svg.appendChild(border);
    }
    if (record.label) {
      const labelHeight = size * 0.14;
      svg.setAttribute("height", String(size + labelHeight));
      svg.setAttribute("viewBox", `0 0 ${size} ${size + labelHeight}`);
      const bg = document.createElementNS(ns, "rect");
      for (const [key, value] of Object.entries({
        x: 0,
        y: size,
        width: size,
        height: labelHeight,
        fill: record.background_color,
      }))
        bg.setAttribute(key, String(value));
      svg.appendChild(bg);
      const text = document.createElementNS(ns, "text");
      for (const [key, value] of Object.entries({
        x: size / 2,
        y: size + labelHeight * 0.65,
        "text-anchor": "middle",
        "font-family": "Arial, sans-serif",
        "font-size": Math.min(size * 0.045, (size * 1.6) / record.label.length),
        fill: record.dots_color,
      }))
        text.setAttribute(key, String(value));
      text.textContent = record.label;
      svg.appendChild(text);
    }
  });
  return qr;
}

export async function exportQR(record: QRCodeRecord, format: "png" | "pdf") {
  const qr = createQR(record, 1024);
  const raw = await qr.getRawData("svg");
  if (!(raw instanceof Blob)) throw new Error("Unable to render this QR code.");
  const text = await raw.text();
  const svg = new DOMParser().parseFromString(text, "image/svg+xml")
    .documentElement as unknown as SVGElement;
  const height = record.label ? 1167.36 : 1024;
  if (format === "pdf") {
    const pdf = new jsPDF({
      unit: "pt",
      format: [550, (height / 1024) * 500 + 50],
    });
    await pdf.svg(svg, {
      x: 25,
      y: 25,
      width: 500,
      height: (height / 1024) * 500,
    });
    pdf.save(`${record.name}.pdf`);
    return;
  }
  // Rasterize the completed SVG, including its frame and label, at export size.
  const image = new Image();
  const source = URL.createObjectURL(
    new Blob([text], { type: "image/svg+xml" }),
  );
  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () =>
        reject(new Error("Unable to load logo or render QR image."));
      image.src = source;
    });
    const canvas = document.createElement("canvas");
    canvas.width = 1024;
    canvas.height = Math.ceil(height);
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Image export is unavailable.");
    context.drawImage(image, 0, 0);
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (value) =>
          value ? resolve(value) : reject(new Error("Image export failed.")),
        "image/png",
      ),
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${record.name}.png`;
    link.click();
    URL.revokeObjectURL(url);
  } finally {
    URL.revokeObjectURL(source);
  }
}

export function contrastRatio(a: string, b: string) {
  const luminance = (color: string) => {
    if (!/^#[\da-f]{6}$/i.test(color)) return 0;
    const values = [1, 3, 5]
      .map((i) => parseInt(color.slice(i, i + 2), 16) / 255)
      .map((v) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
    return values[0] * 0.2126 + values[1] * 0.7152 + values[2] * 0.0722;
  };
  const values = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (values[0] + 0.05) / (values[1] + 0.05);
}
